/**
 * Shows a draggable bar under every block that scrolls sideways.
 * Native scrollbars are hidden between gestures on iOS and absent from
 * carousels, so shoppers never learn the row continues past the edge.
 */
(function () {
  var MIN_OVERFLOW = 8;
  var SKIP_INSIDE = '.site-mobile-nav, .mobile-nav-panel, .site-navigation, .flickity-viewport, [data-scroll-indicator-skip]';

  var indicators = [];

  function ScrollIndicator(scroller) {
    this.scroller = scroller;
    this.el = document.createElement('div');
    this.el.className = 'scroll-indicator';
    this.el.setAttribute('aria-hidden', 'true');
    this.thumb = document.createElement('div');
    this.thumb.className = 'scroll-indicator__thumb';
    this.el.appendChild(this.thumb);

    this.insert();
    this.bind();
    this.update();
  }

  ScrollIndicator.prototype.insert = function () {
    var parent = this.scroller.parentElement;
    var display = parent ? getComputedStyle(parent).display : '';

    // A bar dropped straight into a row would sit next to the block, not under it
    if (display === 'flex' || display === 'inline-flex' || display === 'grid' || display === 'inline-grid') {
      var wrapper = document.createElement('div');
      wrapper.className = 'scroll-indicator-wrapper';
      parent.insertBefore(wrapper, this.scroller);
      wrapper.appendChild(this.scroller);
      wrapper.appendChild(this.el);
    } else {
      this.scroller.insertAdjacentElement('afterend', this.el);
    }
  };

  ScrollIndicator.prototype.bind = function () {
    var self = this;

    this.onScroll = function () {
      self.update();
    };
    this.scroller.addEventListener('scroll', this.onScroll, { passive: true });

    if (window.ResizeObserver) {
      this.observer = new ResizeObserver(function () {
        self.update();
      });
      this.observer.observe(this.scroller);
    }

    this.el.addEventListener('pointerdown', function (event) {
      self.startDrag(event);
    });
  };

  ScrollIndicator.prototype.startDrag = function (event) {
    var self = this;
    var maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;
    if (maxScroll <= MIN_OVERFLOW) return;

    var track = this.el.getBoundingClientRect();
    var thumbWidth = this.thumb.getBoundingClientRect().width;
    var travel = track.width - thumbWidth;
    if (travel <= 0) return;

    // Grabbing the bar anywhere keeps the thumb under the finger from the first move
    var grabOffset = event.clientX - this.thumb.getBoundingClientRect().left;
    if (grabOffset < 0 || grabOffset > thumbWidth) grabOffset = thumbWidth / 2;

    this.el.classList.add('scroll-indicator--dragging');
    this.el.setPointerCapture(event.pointerId);

    var move = function (moveEvent) {
      var position = moveEvent.clientX - track.left - grabOffset;
      var ratio = Math.min(Math.max(position / travel, 0), 1);
      self.scroller.scrollLeft = ratio * maxScroll;
      moveEvent.preventDefault();
    };

    var stop = function () {
      self.el.classList.remove('scroll-indicator--dragging');
      self.el.removeEventListener('pointermove', move);
      self.el.removeEventListener('pointerup', stop);
      self.el.removeEventListener('pointercancel', stop);
    };

    this.el.addEventListener('pointermove', move);
    this.el.addEventListener('pointerup', stop);
    this.el.addEventListener('pointercancel', stop);
    move(event);
  };

  ScrollIndicator.prototype.update = function () {
    var visible = this.scroller.clientWidth;
    var total = this.scroller.scrollWidth;
    var maxScroll = total - visible;

    if (maxScroll <= MIN_OVERFLOW) {
      this.el.hidden = true;
      return;
    }

    this.el.hidden = false;

    var trackWidth = this.el.clientWidth;
    var thumbWidth = Math.max((visible / total) * trackWidth, 32);
    var travel = trackWidth - thumbWidth;
    var progress = this.scroller.scrollLeft / maxScroll;

    this.thumb.style.width = thumbWidth + 'px';
    this.thumb.style.transform = 'translateX(' + progress * travel + 'px)';
  };

  function isScrollable(el) {
    if (el.dataset.scrollIndicator) return false;
    if (el.scrollWidth - el.clientWidth <= MIN_OVERFLOW) return false;
    if (el.closest(SKIP_INSIDE)) return false;
    // Checked against the DOM, not this pass: a later rescan would otherwise
    // pick up the inner scroller that the first pass deliberately skipped
    if (el.parentElement && el.parentElement.closest('[data-scroll-indicator]')) return false;

    var overflowX = getComputedStyle(el).overflowX;
    return overflowX === 'auto' || overflowX === 'scroll';
  }

  /**
   * Flickity carousels move by transform, not by scrolling, and the theme keeps
   * its instance private — so the dots only mirror the selected cell's class.
   */
  function ScrollDots(carousel) {
    this.carousel = carousel;
    this.el = document.createElement('div');
    this.el.className = 'scroll-dots';
    this.el.setAttribute('aria-hidden', 'true');

    this.cells().forEach(function () {
      var dot = document.createElement('span');
      dot.className = 'scroll-dots__dot';
      this.el.appendChild(dot);
    }, this);

    carousel.insertAdjacentElement('afterend', this.el);
    this.bind();
    this.update();
  }

  ScrollDots.prototype.cells = function () {
    var slider = this.carousel.querySelector('.flickity-slider');
    return slider ? Array.prototype.slice.call(slider.children) : [];
  };

  ScrollDots.prototype.bind = function () {
    var self = this;

    this.observer = new MutationObserver(function () {
      self.update();
    });

    this.cells().forEach(function (cell) {
      self.observer.observe(cell, { attributes: true, attributeFilter: ['class'] });
    });
  };

  ScrollDots.prototype.update = function () {
    var cells = this.cells();
    // The theme drops Flickity on wide screens and lays the cells out in a row;
    // a rebuild can also bring back the carousel's own dots, and two rows look broken
    var active = this.carousel.classList.contains('flickity-enabled') &&
      cells.length > 1 &&
      !this.carousel.querySelector('.flickity-page-dots');

    this.el.hidden = !active;
    if (!active) return;

    var selected = cells.findIndex(function (cell) {
      return cell.classList.contains('is-selected');
    });

    Array.prototype.forEach.call(this.el.children, function (dot, index) {
      dot.classList.toggle('scroll-dots__dot--current', index === selected);
    });
  };

  /**
   * Two horizontal scrollers inside one another fight over the swipe: the inner
   * one moves first while the outer bar stands still. Let the outer block own
   * the whole width instead.
   */
  function collapseNested(scroller) {
    Array.prototype.forEach.call(scroller.querySelectorAll('*'), function (el) {
      if (el.dataset.scrollIndicatorCollapsed) return;
      if (el.scrollWidth - el.clientWidth <= MIN_OVERFLOW) return;
      // A block that also scrolls vertically needs its own overflow; leave it alone
      if (el.scrollHeight - el.clientHeight > MIN_OVERFLOW) return;

      var overflowX = getComputedStyle(el).overflowX;
      if (overflowX !== 'auto' && overflowX !== 'scroll') return;

      el.dataset.scrollIndicatorCollapsed = 'true';
      // Both axes: overflow-x alone computes back to auto while overflow-y is hidden
      el.style.overflow = 'visible';
    });
  }

  function scan() {
    var found = [];

    Array.prototype.forEach.call(document.body.querySelectorAll('*'), function (el) {
      if (!isScrollable(el)) return;
      // The outermost scroller already tells the whole story; nested ones only add noise
      if (found.some(function (other) { return other.contains(el); })) return;
      found.push(el);
    });

    found.forEach(function (el) {
      collapseNested(el);
      el.dataset.scrollIndicator = 'true';
      indicators.push(new ScrollIndicator(el));
    });

    Array.prototype.forEach.call(document.querySelectorAll('.flickity-enabled'), function (el) {
      if (el.dataset.scrollDots) return;
      if (el.closest(SKIP_INSIDE)) return;
      // Some carousels (testimonials) are set up with pageDots — ours would double them
      if (el.querySelector('.flickity-page-dots')) return;

      el.dataset.scrollDots = 'true';
      indicators.push(new ScrollDots(el));
    });
  }

  function refresh() {
    indicators.forEach(function (indicator) {
      indicator.update();
    });
    scan();
  }

  var refreshTimer;
  function refreshSoon() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, 200);
  }

  function start() {
    scan();

    window.addEventListener('resize', refreshSoon);
    window.addEventListener('load', refreshSoon);
    document.addEventListener('shopify:section:load', refreshSoon);

    // Ajax content changes the page height, which is enough to catch new blocks
    if (window.ResizeObserver) {
      new ResizeObserver(refreshSoon).observe(document.body);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
